/**
 * ModelSelector — provider-tabbed model picker with a reasoning-effort slider.
 *
 * Left rail = provider logo tabs (OpenAI / Claude / Gemma / Mock). Switching a
 * tab shows that provider's models in a fixed-height scroll area. The bottom
 * strip is a "Reasoning" effort slider (dots, current selection only) that
 * applies to the selected model — Claude-GUI style.
 *
 * The catalog itself lives in `@papan/types` (models.ts) — browser-safe data,
 * shared with the server, so this file holds only the logos and the rendering.
 * It used to carry its own copy of the model list, hand-synced with
 * `providers.ts` via `@sync` comments; see that module's header for why that
 * had to go.
 */
import { useState, useRef, useEffect, useCallback, type ReactElement } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, Check, FlaskConical } from 'lucide-react'
import { useTheme, getThemeClass, useToast, useAnchoredPosition } from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'
import { authClient } from '@papan/auth/client'
import { isMockEligibleEmail } from '../core/mock-allowlist.js'
import {
  PROVIDER_TABS,
  providerTabOf,
  getShortLabel,
  getModelEfforts,
  getModelDefaultEffort,
  DEFAULT_MODEL,
  type ModelCatalogEntry,
} from '@papan/types'
import { useModelCatalog, modelLockReason, type ResolvedModel } from './useModelCatalog.js'

// Re-exported: ChatPanel and the shells import DEFAULT_MODEL from here.
export { DEFAULT_MODEL, getModelEfforts, getModelDefaultEffort }

/** Artificial Analysis "blended" price — a 3:1 input:output weighting. Used as
 *  the relative-cost signal (artificialanalysis.ai/leaderboards/models). */
function blendedCost(m: ModelCatalogEntry): number {
  return (3 * m.inputCost + m.outputCost) / 4
}

/** A provider tab plus its brand mark. The data half comes from the catalog;
 *  only the SVG is local, because JSX cannot live in a browser-safe data
 *  module the server also imports. */
interface ProviderTabView {
  id: string
  label: string
  Logo: () => ReactElement
  models: ResolvedModel[]
  mockOnly?: boolean
  comingSoon?: boolean
}

// ─── Brand marks (inline SVG, currentColor) ──────────────────────────────

function OpenAILogo() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7477-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997z" />
    </svg>
  )
}

function ClaudeLogo() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.3041 3.541h-3.6718l6.696 16.918H24Zm-10.6082 0L0 20.459h3.7442l1.3693-3.5527h7.0052l1.3693 3.5528h3.7442L10.5363 3.541Zm-.3712 10.2232 2.2914-5.9456 2.2914 5.9456Z" />
    </svg>
  )
}

function GemmaLogo() {
  return (
    <svg width="15" height="15" viewBox="0 0 18 18" aria-hidden="true">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4" />
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853" />
      <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05" />
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335" />
    </svg>
  )
}

function MockLogo() {
  return <FlaskConical size={15} />
}

// ─── Tabs (catalog data + local brand marks) ─────────────────────────────

const LOGOS: Record<string, () => ReactElement> = {
  openai: OpenAILogo,
  anthropic: ClaudeLogo,
  google: GemmaLogo,
  mock: MockLogo,
}

/** Tabs built from the RESOLVED list — a tab whose models were all hidden by
 *  the platform layer renders like any other empty tab. */
function buildTabs(models: ResolvedModel[]): ProviderTabView[] {
  return PROVIDER_TABS.map(tab => ({
    ...tab,
    Logo: LOGOS[tab.id] ?? MockLogo,
    models: models.filter(m => m.provider === tab.id),
  }))
}

/** Relative-cost level (0=free … 5=priciest) from the blended per-1M rate, in
 *  0.5 steps so a half-$ can show finer gradation between the coarse buckets. */
function costLevel(cost: number): number {
  if (cost <= 0) return 0
  if (cost < 0.5) return 1
  if (cost < 1) return 1.5
  if (cost < 2) return 2
  if (cost < 4) return 2.5
  if (cost < 8) return 3
  if (cost < 14) return 3.5
  if (cost < 25) return 4
  if (cost < 45) return 4.5
  return 5
}

/** Relative-cost meter as $ signs (filled = level; a half-filled $ shows a .5).
 *  No real dollar figure — a "pricier vs the others" cue. */
function CostDollars({ cost }: { cost: number }) {
  const level = costLevel(cost)
  if (level <= 0) return null
  const full = Math.floor(level)
  const half = level - full >= 0.5
  return (
    <span className="text-[11px] font-semibold tracking-tight tabular-nums shrink-0 inline-flex" title={`Relative cost ${level}/5`} aria-label={`Relative cost ${level} of 5`}>
      {Array.from({ length: 5 }, (_, i) => {
        const n = i + 1
        if (n <= full) return <span key={i} className="themed-text-secondary">$</span>
        if (n === full + 1 && half) {
          return (
            <span key={i} className="relative inline-block">
              <span className="opacity-25">$</span>
              <span className="absolute top-0 left-0 h-full overflow-hidden themed-text-secondary" style={{ width: '50%' }}>$</span>
            </span>
          )
        }
        return <span key={i} className="opacity-25">$</span>
      })}
    </span>
  )
}

const EFFORT_LABELS: Record<string, string> = {
  default: 'Auto', none: 'None', minimal: 'Minimal', low: 'Low', medium: 'Medium', high: 'High', max: 'Max',
}

/** Discrete reasoning-effort slider — draggable thumb along a dotted track,
 *  snapping to the nearest stop. Click anywhere on the track also sets it.
 *  Only the current selection's label is surfaced (by the caller). */
function ReasoningSlider({ options, value, onChange }: {
  options: string[]
  value: string
  onChange: (v: string) => void
}) {
  const idx = Math.max(0, options.indexOf(value))
  const trackRef = useRef<HTMLDivElement>(null)
  const [dragging, setDragging] = useState(false)
  // Read the latest props inside the window listeners without re-subscribing.
  const latest = useRef({ options, value, onChange })
  latest.current = { options, value, onChange }

  const setFromClientX = (clientX: number) => {
    const track = trackRef.current
    const { options: opts, value: val, onChange: cb } = latest.current
    if (!track || opts.length < 2) return
    const r = track.getBoundingClientRect()
    const ratio = Math.max(0, Math.min(1, (clientX - r.left - 8) / (r.width - 16)))
    const next = opts[Math.round(ratio * (opts.length - 1))]
    if (next && next !== val) cb(next)
  }

  useEffect(() => {
    if (!dragging) return
    const move = (e: PointerEvent) => setFromClientX(e.clientX)
    const up = () => setDragging(false)
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    return () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up) }
  }, [dragging])

  return (
    <div
      ref={trackRef}
      onPointerDown={(e) => { setDragging(true); setFromClientX(e.clientX) }}
      className="relative flex items-center h-5 px-1 cursor-pointer select-none touch-none"
    >
      {/* rail + filled portion up to current */}
      <div className="absolute left-2 right-2 h-px bg-current opacity-15" />
      <div className="absolute left-2 h-px bg-brand-400/60" style={{ width: `calc((100% - 16px) * ${options.length > 1 ? idx / (options.length - 1) : 0})` }} />
      {/* dots (decorative — the track handles all pointer input) */}
      <div className="relative flex-1 flex items-center justify-between pointer-events-none">
        {options.map((opt, i) => (
          <span
            key={opt}
            className={`rounded-full transition-all ${
              i === idx
                ? `bg-brand-400 ring-2 ring-brand-400/20 ${dragging ? 'w-3.5 h-3.5' : 'w-3 h-3'}`
                : i < idx
                ? 'w-1.5 h-1.5 bg-brand-400/60'
                : 'w-1.5 h-1.5 bg-current opacity-25'
            }`}
          />
        ))}
      </div>
    </div>
  )
}

interface Props {
  selectedModel: string
  onSelect: (modelId: string) => void
  /** Reasoning-effort variant + setter — omit to hide the reasoning slider. */
  variant?: string
  onVariantChange?: (variant: string) => void
  disabled?: boolean
}

export function ModelSelector({ selectedModel, onSelect, variant, onVariantChange, disabled }: Props) {
  const { models, defaultModel } = useModelCatalog()
  const { t } = useTranslation('agent')
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const [activeTab, setActiveTab] = useState(() => providerTabOf(selectedModel))
  const [userEmail, setUserEmail] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  // The popover is portaled to <body> so the chat panel's overflow-hidden can't
  // clip it. popRef tracks it for outside-click; pos is its fixed placement.
  const popRef = useRef<HTMLDivElement>(null)
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const POPOVER_WIDTH = 248

  // Subscribe to Better Auth's session atom so the picker re-renders on email swap.
  useEffect(() => {
    let mounted = true
    type SessionAtom = {
      get: () => { data?: { user?: { email?: string } } | null } | null
      subscribe: (cb: (s: { data?: { user?: { email?: string } } | null } | null) => void) => () => void
    }
    const atom = authClient.useSession as unknown as SessionAtom
    const apply = (s: { data?: { user?: { email?: string } } | null } | null) => {
      if (mounted) setUserEmail(s?.data?.user?.email ?? null)
    }
    apply(atom.get?.() ?? null)
    const unsub = atom.subscribe?.(apply)
    void authClient.getSession()
    return () => { mounted = false; unsub?.() }
  }, [])

  // On open, jump to the selected model's provider tab.
  useEffect(() => { if (open) setActiveTab(providerTabOf(selectedModel)) }, [open, selectedModel])

  // Measure the trigger and place the portaled popover above it (fixed coords,
  // clamped to the viewport). Re-measure on scroll/resize while open.
  // Shared anchored-panel convention. `placement: 'top'` is pinned, not auto:
  // this opens out of the composer, and a flip downward would cover the input
  // it belongs to. The popover also now DISMISSES when the trigger scrolls out
  // of view — the old version repositioned forever, so it could outlive its own
  // trigger.
  const { pos, exiting, origin } = useAnchoredPosition({
    open,
    anchorRef: containerRef,
    floatingRef: popRef,
    onDismiss: useCallback(() => setOpen(false), []),
    placement: 'top',
    gap: 6,
    exitTransitionMs: 160,
  })

  // Close on outside click / Escape — the popover is portaled, so check it too.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (containerRef.current?.contains(target) || popRef.current?.contains(target)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey) }
  }, [open])

  /**
   * Why a locked model can't be picked. Four cases, because "upgrade" is
   * useless advice to someone who cannot buy anything, and a plan we can't
   * name must not be invented.
   */
  const lockedTitle = (model: ResolvedModel): string => {
    const reason = modelLockReason(model)
    return reason ? t(reason.key, reason.params) : ''
  }
  const explainLocked = (model: ResolvedModel) => {
    // `info`, not `error`: the user did nothing wrong, and this is the same
    // message the server would return if they got past the picker.
    toast.info(lockedTitle(model))
  }

  /**
   * A model that stopped being usable while it was selected.
   *
   * The session stores its model, so an admin restricting it mid-session leaves
   * the picker pointing at something every send will 403 on. Swap to the
   * server-supplied default once and say so, rather than failing per message —
   * and only after the catalog has actually loaded, so the static fallback
   * can't trigger a spurious swap.
   */
  const swapped = useRef(false)
  useEffect(() => {
    if (swapped.current || models.length === 0) return
    const current = models.find(m => m.id === selectedModel)
    if (!current || current.access === 'allowed') return
    const target = defaultModel && models.find(m => m.id === defaultModel && m.access === 'allowed')
      ? defaultModel
      : models.find(m => m.access === 'allowed')?.id
    if (!target || target === selectedModel) return
    swapped.current = true
    onSelect(target)
    toast.info(t('model.switchedAway', { model: current.label }))
  }, [models, defaultModel, selectedModel, onSelect, toast, t])

  const tabs = buildTabs(models).filter(p => !p.mockOnly || isMockEligibleEmail(userEmail))
  const activeTabDef = tabs.find(t => t.id === activeTab) ?? tabs[0]
  const activeModels = activeTabDef?.models ?? []
  const activeTabComingSoon = !!activeTabDef?.comingSoon
  const efforts = getModelEfforts(selectedModel)
  const effortOptions = ['default', ...efforts]
  // On the "Auto" (default) tick, surface what Auto actually resolves to for this
  // model — "Auto · High", "Auto · Off" — so switching models shows the cost/quality
  // change instead of hiding it. Bare "Auto" when the model has no pin (provider
  // default). An explicit pick shows just its own label.
  const defEff = getModelDefaultEffort(selectedModel)
  const effortLabel = (variant ?? 'default') === 'default' && defEff
    ? `Auto · ${defEff === 'none' ? 'Off' : (EFFORT_LABELS[defEff] ?? defEff)}`
    : (EFFORT_LABELS[variant ?? 'default'] ?? variant)

  return (
    <div ref={containerRef} className="relative shrink-0">
      {/* Trigger pill */}
      <button
        type="button"
        onClick={() => !disabled && setOpen(prev => !prev)}
        disabled={disabled}
        className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] themed-text-muted hover:themed-text themed-bg-secondary border themed-border transition-colors disabled:opacity-40"
      >
        <span>{getShortLabel(selectedModel)}</span>
        <ChevronDown size={10} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Popover — portaled to <body> (fixed) so the chat panel's overflow can't
          clip it; theme class re-applied since body is outside ThemedArea. */}
      {open && createPortal(
        <div
          ref={popRef}
          data-testid="model-picker-popover"
          className={`${themeClass} ws-glass-modal ws-anim-modal-in fixed z-[60]${exiting ? ' anchor-exit' : ''}`}
          style={pos
            ? { left: pos.left, top: pos.top, width: POPOVER_WIDTH, ...(origin ? { transformOrigin: `${origin.x}px ${origin.y}px` } : {}) }
            : { left: 0, top: 0, width: POPOVER_WIDTH, visibility: 'hidden' }}
        >
          <div className="flex">
            {/* Left rail — provider logo tabs on a frosted well (a translucent
                tint; a nested backdrop-blur can't re-sample the page since the
                popover is the backdrop root, so the tint carries the frost). */}
            <div className="flex flex-col shrink-0 py-1 ws-glass-well backdrop-blur-sm">
              {tabs.map(t => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setActiveTab(t.id)}
                  title={t.label}
                  className={`p-2.5 flex items-center justify-center transition-colors ${
                    activeTab === t.id
                      ? 'themed-text bg-brand-500/10'
                      : 'themed-text-muted opacity-50 hover:opacity-100 hover:themed-bg-tertiary'
                  }`}
                >
                  <t.Logo />
                </button>
              ))}
            </div>

            {/* Right column — model list + the model-specific reasoning slider
                UNDER it (both align to the list, not the full popover width). */}
            <div className="flex-1 min-w-0 flex flex-col">
              {/* Model list — FIXED height so switching tabs doesn't resize. */}
              <div className="h-[210px] overflow-y-auto py-1">
              {activeTabComingSoon ? (
                <div className="flex flex-col items-center justify-center h-full gap-1 px-4 text-center">
                  <span className="text-xs themed-text-secondary font-medium">Coming soon</span>
                  <span className="text-[10px] themed-text-muted">{tabs.find(t => t.id === activeTab)?.label} models aren't available yet.</span>
                </div>
              ) : activeModels.map(model => {
                const isSelected = model.id === selectedModel
                const locked = model.access !== 'allowed'
                return (
                  <button
                    key={model.id}
                    type="button"
                    // A locked row stays CLICKABLE and explains itself instead
                    // of being disabled: `disabled` swallows the click, so the
                    // user learns nothing about why the model is there.
                    onClick={() => {
                      if (locked) { explainLocked(model); return }
                      onSelect(model.id)
                      setOpen(false)
                    }}
                    title={locked ? lockedTitle(model) : undefined}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs transition-colors ${
                      isSelected ? 'themed-text bg-brand-500/10' : 'themed-text-muted hover:themed-text hover:themed-bg-tertiary'
                    } ${locked ? 'opacity-55' : ''}`}
                  >
                    <span className="w-4 shrink-0">{isSelected && !locked && <Check size={10} className="text-brand-400" />}</span>
                    <span className="flex-1 text-left truncate">{model.label}</span>
                    {locked
                      ? (
                        <span className={`text-[9px] px-1 py-0.5 rounded whitespace-nowrap shrink-0 ${
                          model.access === 'upsell'
                            ? 'bg-brand-500/20 text-brand-300'
                            : 'bg-amber-500/20 text-amber-300'
                        }`}>
                          {model.access === 'upsell' ? t('model.upgradeChip') : t('model.restrictedChip')}
                        </span>
                      )
                      : <CostDollars cost={blendedCost(model)} />}
                  </button>
                )
              })}
              </div>

              {/* Reasoning effort — under the model list; model-specific (the
                  SELECTED model's efforts, empty for non-reasoning models). */}
              {onVariantChange && efforts.length > 0 && (
                <div className="border-t themed-border px-3 py-2.5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] uppercase tracking-wider themed-text-muted font-medium">Reasoning</span>
                    <span className="text-[11px] themed-text font-medium">{effortLabel}</span>
                  </div>
                  <ReasoningSlider options={effortOptions} value={variant ?? 'default'} onChange={onVariantChange} />
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}
